/**
 * BisonStatusWidget · v2.6.9 batch CI
 * ===================================
 * Reusable live "Online Status" widget extracted from DevLicenseDialog.
 * Polls /api/system-status every 15 s and renders 5 indicators (Online,
 * Data Center, Bison AI, Bison AI GPS, Bison AI Video & Audio). Used by:
 *   - DevLicenseDialog (admin)
 *   - MarketingBisonTaxiCommander public page (/bison-taxi-commander)
 *
 * The polling logic is identical to the original Developer License modal
 * so any future change there can be made in one place.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";

export function StatusDot({ ok, color = "emerald" }) {
  if (ok === null) return <span className="inline-flex h-2.5 w-2.5 rounded-full bg-zinc-600" data-testid="status-dot-loading"/>;
  if (ok) {
    const cls = color === "blue" ? "bg-blue-400" : "bg-emerald-400";
    return (
      <span className="relative inline-flex h-2.5 w-2.5" data-testid={`status-dot-on-${color}`}>
        <span className={`absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping ${cls}`}/>
        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${cls}`}/>
      </span>
    );
  }
  return <span className="inline-flex h-2.5 w-2.5 rounded-full bg-red-500" data-testid="status-dot-off"/>;
}

export function StatusRow({ label, ok, sub, color = "emerald" }) {
  const statusText = ok === null ? "checking…" : ok ? "active" : "offline";
  const statusColor = ok === null ? "text-zinc-400" : ok ? (color === "blue" ? "text-blue-400" : "text-emerald-400") : "text-red-400";
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <div className="text-xs text-zinc-300 font-semibold truncate">{label}</div>
        {sub && <div className="text-[10px] text-zinc-500 truncate">{sub}</div>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <StatusDot ok={ok} color={color}/>
        <span className={`text-[10px] uppercase tracking-wider font-bold ${statusColor}`}>{statusText}</span>
      </div>
    </div>
  );
}

export function useBisonStatus({ active = true } = {}) {
  const [status, setStatus] = useState({ online: null, dc: null, ai: null, gps: null, video: null, dcLocation: "North America / Oklahoma City · net-1" });
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    const refresh = () => {
      api.get("/system-status").then((r) => {
        if (!alive) return;
        setStatus({
          online: !!r.data.online,
          dc: !!r.data.data_center?.active,
          ai: !!r.data.bison_ai?.active,
          gps: !!r.data.bison_ai_gps?.active,
          video: !!r.data.bison_ai_video_audio?.active,
          dcLocation: r.data.data_center?.location || "North America / Oklahoma City · net-1",
        });
      }).catch(() => {
        if (!alive) return;
        setStatus({ online: false, dc: false, ai: false, gps: false, video: false, dcLocation: "North America / Oklahoma City · net-1" });
      });
    };
    refresh();
    const t = setInterval(refresh, 15_000);
    return () => { alive = false; clearInterval(t); };
  }, [active]);
  return status;
}

/**
 * Default-export wrapper that renders the 5-indicator card with default
 * polling. Pass active=false to suspend polling (e.g., when wrapped in a
 * collapsed modal). Pass className to override the outer Card classes.
 */
export default function BisonStatusWidget({ active = true, className = "", testid = "bison-status-widget" }) {
  const status = useBisonStatus({ active });
  return (
    <Card className={`bg-zinc-900/70 border-zinc-800 p-4 ${className}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold mb-2">Live System Status</div>
      <StatusRow label="Online Status" ok={status.online}/>
      <StatusRow label="Data Center Status" sub={status.dcLocation} ok={status.dc}/>
      <StatusRow label="Bison AI — Artificial Intelligence" ok={status.ai}/>
      <StatusRow label="BISON AI GPS" ok={status.gps}/>
      <StatusRow label="Bison AI Video & Audio Monitoring" ok={status.video} color="blue"/>
    </Card>
  );
}
